import { useState } from 'react'
import { Plus, Save, AlertCircle, CheckCircle, FileText, Loader } from 'lucide-react'
import ThemeEditor from './ThemeEditor'
import QuestionnaireMetadataForm from './QuestionnaireMetadataForm'
import { 
  questionnaireSubmissionSchema, 
  validateUniqueIds, 
  calculateQuestionnaireStats,
  generateUniqueId
} from '../../utils/questionnaireValidation'
import { generateQuestionId, slugify } from '../../utils/idGenerator'
import { createQuestionnaireVersion } from '../../services/questionnaireService'
import { useToast } from '../../hooks/useToast'

/**
 * QuestionnaireEditor Component
 * Main editor for creating/editing questionnaires
 */
function QuestionnaireEditor({ initialQuestionnaire = null, onSuccess }) {
  const { showToast, ToastContainer } = useToast()
  
  // Initialize questionnaire state
  const [questionnaire, setQuestionnaire] = useState(initialQuestionnaire || {
    title: '',
    description: '',
    themes: []
  })
  
  // Initialize metadata state (version is auto-generated by backend)
  const [metadata, setMetadata] = useState({
    changelog: ''
  })
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [validationErrors, setValidationErrors] = useState([])

  // Theme handlers
  const handleThemeUpdate = (themeIndex, updatedTheme) => {
    const updatedThemes = [...questionnaire.themes]
    updatedThemes[themeIndex] = updatedTheme
    setQuestionnaire({ ...questionnaire, themes: updatedThemes })
  }

  const handleAddTheme = () => {
    const existingThemeIds = questionnaire.themes.map(t => t.id)
    const newThemeId = generateUniqueId('theme', existingThemeIds)
    
    const newTheme = {
      id: newThemeId,
      title: '',
      icon: '📋',
      description: '',
      questions: [{
        id: `${newThemeId}_q1`,
        text: '',
        description: '', // Add description field
        weight: 1
      }]
    }
    
    setQuestionnaire({ ...questionnaire, themes: [...questionnaire.themes, newTheme] })
  }

  const handleDeleteTheme = (themeIndex) => {
    if (questionnaire.themes.length <= 1) {
      showToast('Le questionnaire doit contenir au moins un thème', 'warning')
      return
    }
    
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce thème ?')) {
      return
    }
    
    const updatedThemes = questionnaire.themes.filter((_, i) => i !== themeIndex)
    setQuestionnaire({ ...questionnaire, themes: updatedThemes })
  }

  // Validation - accepts optional questionnaire parameter to avoid async state issues
  const validateForm = (questionnaireToValidate = questionnaire) => {
    const errors = []
    
    // Zod schema validation
    try {
      questionnaireSubmissionSchema.parse({ questionnaire: questionnaireToValidate, metadata })
    } catch (error) {
      // Check if it's a Zod error with errors array
      if (error?.errors && Array.isArray(error.errors)) {
        error.errors.forEach(err => {
          errors.push(`${err.path.join('.')} : ${err.message}`)
        })
      } else {
        // Generic error handling
        errors.push(`Validation error: ${error?.message || 'Unknown error'}`)
      }
    }
    
    // Unique IDs validation (only if questionnaire has themes)
    if (questionnaireToValidate?.themes) {
      const uniqueCheck = validateUniqueIds(questionnaireToValidate)
      if (!uniqueCheck.valid) {
        errors.push(...uniqueCheck.errors)
      }
    }
    
    setValidationErrors(errors)
    return errors.length === 0
  }

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Fill in the missing IDs before validation.
    // Existing IDs are never rewritten: submitted assessments store their
    // themes and scores under those keys, and changing them makes past
    // assessments incomparable with the new ones.
    const usedQuestionIds = new Set()
    const questionnaireWithIds = {
      ...questionnaire,
      themes: questionnaire.themes.map((theme, themeIndex) => ({
        ...theme,
        id: theme.id || slugify(theme.title),
        questions: theme.questions.map((question, questionIndex) => {
          let id = question.id
          if (!id || usedQuestionIds.has(id)) {
            const base = generateQuestionId(themeIndex, questionIndex)
            id = base
            let counter = 2
            while (usedQuestionIds.has(id)) {
              id = `${base}_${counter}`
              counter++
            }
          }
          usedQuestionIds.add(id)
          return { ...question, id }
        })
      }))
    }
    
    // Update state with generated IDs
    setQuestionnaire(questionnaireWithIds)
    
    // Validate form with generated IDs - pass questionnaireWithIds to avoid async state issues
    if (!validateForm(questionnaireWithIds)) {
      showToast('Veuillez corriger les erreurs avant de soumettre', 'error')
      return
    }
    
    setIsSubmitting(true)
    
    try {
      // Calculate totalMaxScore before submission
      const stats = calculateQuestionnaireStats(questionnaireWithIds)
      const questionnaireWithStats = {
        ...questionnaireWithIds,
        totalMaxScore: stats.totalMaxScore
      }
      
      // Calculate maxScore for each theme and ensure all fields are present
      const themesWithMaxScore = questionnaireWithStats.themes.map(theme => ({
        ...theme,
        maxScore: theme.questions.reduce((sum, q) => sum + (q.weight * 10), 0),
        // Ensure all required fields are present
        questions: theme.questions.map(q => ({
          id: q.id,
          text: q.text,
          title: q.text, // Backend might expect 'title' field
          description: q.description || '',
          weight: q.weight,
          maxScore: q.weight * 10
        }))
      }))
      
      const finalQuestionnaire = {
        ...questionnaireWithStats,
        themes: themesWithMaxScore
      }
      
      console.log('Submitting questionnaire:', finalQuestionnaire)
      
      // Submit to API
      const response = await createQuestionnaireVersion({
        questionnaire: finalQuestionnaire,
        metadata
      })
      
      // Show success message with auto-generated version number
      const createdVersion = response.data?.version || 'nouvelle version'
      showToast(`✨ Version ${createdVersion} créée avec succès!`, 'success')
      
      // Callback for parent component
      if (onSuccess) {
        onSuccess(response.data)
      }
      
    } catch (error) {
      console.error('Error creating questionnaire version:', error)
      showToast(error.message || 'Erreur lors de la création de la version', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Calculate stats
  const stats = calculateQuestionnaireStats(questionnaire)

  return (
    <div className="space-y-6">
      <ToastContainer />
      
      {/* Header with stats */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Éditeur de questionnaire
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Créez ou modifiez un questionnaire avec ses thèmes et questions
            </p>
          </div>
          
          {/* Stats */}
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.totalThemes}</p>
              <p className="text-xs text-gray-500">Thèmes</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{stats.totalQuestions}</p>
              <p className="text-xs text-gray-500">Questions</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{stats.totalMaxScore}</p>
              <p className="text-xs text-gray-500">Score max</p>
            </div>
          </div>
        </div>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-800 mb-2">
                Erreurs de validation ({validationErrors.length})
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-red-700">
                {validationErrors.map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Metadata Form */}
        <QuestionnaireMetadataForm
          questionnaire={questionnaire}
          metadata={metadata}
          onQuestionnaireChange={setQuestionnaire}
          onMetadataChange={setMetadata}
        />

        {/* Themes Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Thèmes ({questionnaire.themes.length})
            </h3>
            <button
              type="button"
              onClick={handleAddTheme}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Ajouter un thème</span>
            </button>
          </div>

          {/* Themes List */}
          <div className="space-y-4">
            {questionnaire.themes.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">
                  Aucun thème. Commencez par ajouter votre premier thème.
                </p>
                <button
                  type="button"
                  onClick={handleAddTheme}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  <span>Ajouter un thème</span>
                </button>
              </div>
            ) : (
              questionnaire.themes.map((theme, index) => (
                <ThemeEditor
                  key={index}
                  theme={theme}
                  index={index}
                  onUpdate={(updated) => handleThemeUpdate(index, updated)}
                  onDelete={() => handleDeleteTheme(index)}
                  allThemeIds={questionnaire.themes.map(t => t.id)}
                />
              ))
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 shadow-lg rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>
                {stats.totalThemes} thème(s), {stats.totalQuestions} question(s)
              </span>
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting || questionnaire.themes.length === 0}
              className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Création en cours...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Créer la version {metadata.version || 'X.Y.Z'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default QuestionnaireEditor

